import { useAccount } from "@apis/accounts/useAccounts"
import type { IAccount } from "@apis/accounts/types"
import type { IMarketCurrentPtc } from "@apis/pricing/types"
import { useGetMarketCurrentPtcQuery } from "@apis/pricing/usePricing"
import { serializeScript } from "@utils/verificationScriptsUtils"
import { OFFER_TYPES } from "@apis/offers/types"

import { getRetentionScript, getWinbackScript } from "./verificationScripts"
import { useAccountOfferFunnel } from "./useAccountOfferFunnel"
import { isRetentionOffer, isWinbackOffer } from "./offerTypeGuards"

export const useSerializeOfferScript = () => {
  const { account_id, eligibleOfferType, selectedOffer } =
    useAccountOfferFunnel()

  const getAccountQuery = useAccount(account_id)
  const getCurrentPtcQuery = useGetMarketCurrentPtcQuery(
    getAccountQuery.data?.market_code,
    getAccountQuery.data?.revenue_class_code,
  )

  // Only build a script when the selected offer matches the account's offer type.
  // The previous ternary treated anything other than "Winback" as retention, so a
  // mismatch read the wrong verification script to the member (see NEE-4061).
  const buildScript = (): string[] => {
    if (
      eligibleOfferType === OFFER_TYPES.Winback &&
      isWinbackOffer(selectedOffer)
    ) {
      return serializeScript(
        getWinbackScript(
          getAccountQuery.data as IAccount,
          selectedOffer,
          getCurrentPtcQuery.data as IMarketCurrentPtc,
        ),
      )
    }

    if (
      eligibleOfferType === OFFER_TYPES.Retention &&
      isRetentionOffer(selectedOffer)
    ) {
      return serializeScript(
        getRetentionScript(getAccountQuery.data as IAccount, selectedOffer),
      )
    }

    // No matching flow, so there are no verification steps to read.
    return []
  }

  return {
    script: buildScript(),
    account_number: getAccountQuery.data?.account_number,
    prospect_guid: getAccountQuery.data?.prospect_guid,
    market: getAccountQuery.data?.market,
  }
}
